/* ==========================================================================
   Comfora – Theme-JavaScript (seitenübergreifend)
   Vanilla JS + Custom Elements, keine Abhängigkeiten.
   Shopify-Version: Warenkorb über die Ajax Cart API.
   ========================================================================== */
(function () {
  'use strict';

  const Comfora = (window.Comfora = window.Comfora || {});
  const root = document.documentElement;
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------------
     Utilities
     ------------------------------------------------------------------------ */
  const moneyFormatter = new Intl.NumberFormat('en-IE', { style: 'currency', currency: root.dataset.currency || 'EUR' });

  Comfora.formatMoney = (cents) => moneyFormatter.format((Number(cents) || 0) / 100);

  // Product data in Shopify is German – translate colour values and discount names for display.
  const displayTerms = [
    ['Weiß', 'White'],
    ['Schwarz', 'Black'],
    ['Steingrau', 'Stone Grey'],
    ['Karamell', 'Caramel'],
    ['2er-Set', '2-pair bundle'],
    ['3er-Set', '3-pair bundle'],
  ];
  Comfora.displayText = (value = '') => displayTerms.reduce((text, [de, en]) => text.split(de).join(en), String(value));
  Comfora.assetUrl = (path) => (root.dataset.root || '') + path;
  Comfora.reducedMotion = () => reducedMotionQuery.matches;
  Comfora.scrollBehavior = () => (Comfora.reducedMotion() ? 'auto' : 'smooth');

  Comfora.readJSON = (element) => {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('[Comfora] Ungültiges JSON', element, error);
      return null;
    }
  };

  Comfora.storage = {
    get(key, fallback) {
      try {
        const raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
      } catch (error) {
        return fallback;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
        return true;
      } catch (error) {
        return false;
      }
    },
  };

  /* ------------------------------------------------------------------------
     Toast-Benachrichtigungen
     ------------------------------------------------------------------------ */
  Comfora.toast = (message, duration = 3400) => {
    let region = document.querySelector('.toast-region');
    if (!region) {
      region = document.createElement('div');
      region.className = 'toast-region';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.append(region);
    }
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    region.append(toast);

    window.setTimeout(() => {
      toast.classList.add('is-leaving');
      window.setTimeout(() => toast.remove(), 320);
    }, duration);
  };

  /* ------------------------------------------------------------------------
     Overlays (Drawer & Modals auf Basis von <dialog>)
     – Fokus-Falle, Escape & inert-Hintergrund liefert showModal() nativ.
     ------------------------------------------------------------------------ */
  class Overlay {
    constructor(dialog) {
      this.dialog = dialog;
      this.opener = null;
      this.closeTimer = null;

      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-overlay-close]')) {
          event.preventDefault();
          this.close();
          return;
        }
        if (dialog.hasAttribute('data-close-on-link') && event.target.closest('a[href]')) {
          this.close({ restoreFocus: false });
        }
      });
    }

    open(opener) {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
        if (this.dialog.open) {
          this.dialog.classList.add('is-visible');
          return;
        }
      }
      if (this.dialog.open) return;

      this.opener = opener || document.activeElement;
      Overlay.openCount += 1;
      root.classList.add('has-overlay');
      this.dialog.showModal();
      void this.dialog.offsetWidth; // Reflow erzwingen, damit die Einblend-Transition startet
      this.dialog.classList.add('is-visible');
      this.dialog.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
    }

    close({ restoreFocus = true } = {}) {
      if (!this.dialog.open || this.closeTimer) return;
      this.dialog.classList.remove('is-visible');

      this.closeTimer = window.setTimeout(() => {
        this.closeTimer = null;
        this.dialog.close();
        Overlay.openCount = Math.max(0, Overlay.openCount - 1);
        if (Overlay.openCount === 0) root.classList.remove('has-overlay');
        if (restoreFocus && this.opener && this.opener.isConnected) {
          this.opener.focus({ preventScroll: true });
        }
        this.dialog.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
      }, Comfora.reducedMotion() ? 0 : 340);
    }
  }
  Overlay.openCount = 0;

  const overlays = new Map();

  Comfora.getOverlay = (id) => {
    if (!overlays.has(id)) {
      const dialog = document.getElementById(id);
      if (!dialog || dialog.tagName !== 'DIALOG') return null;
      overlays.set(id, new Overlay(dialog));
    }
    return overlays.get(id);
  };
  Comfora.openOverlay = (id, opener) => Comfora.getOverlay(id)?.open(opener);
  Comfora.closeOverlay = (id) => Comfora.getOverlay(id)?.close();

  document.querySelectorAll('dialog.overlay[id]').forEach((dialog) => Comfora.getOverlay(dialog.id));

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-overlay-open]');
    if (!trigger) return;
    event.preventDefault();
    Comfora.openOverlay(trigger.dataset.overlayOpen, trigger);
  });

  /* ------------------------------------------------------------------------
     Warenkorb – Shopify Ajax Cart API (/cart.js, /cart/add.js, /cart/change.js)
     Automatische Rabatte (z. B. Mengenstaffel) kommen direkt aus Shopify.
     ------------------------------------------------------------------------ */
  const routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* Im Theme-Editor läuft die Vorschau in einem iframe. Shopify lässt den Checkout dort nicht zu
     (Chrome meldet ERR_BLOCKED_BY_RESPONSE). Deshalb öffnen wir den Warenkorb in einem echten Tab –
     dort führt „Secure checkout" ganz normal in den Shopify-Checkout. */
  const inThemeEditor = () => window.self !== window.top;

  const openCartTab = () => {
    const shop = (window.Shopify && window.Shopify.shop) || window.location.host;
    const themeId = window.Shopify && window.Shopify.theme && window.Shopify.theme.id;
    const preview = themeId ? `&preview_theme_id=${themeId}` : '';
    const url = `https://${shop}${routesRoot}cart?_ab=0&_fd=0&_sc=1${preview}`;
    const tab = window.open(url, '_blank');
    if (!tab) {
      try {
        window.top.location.href = url;
      } catch (error) {
        window.location.href = url;
      }
    }
    Comfora.toast('Checkout cannot open inside the theme editor – your cart is now in a new tab.');
  };

  document.addEventListener(
    'submit',
    (event) => {
      if (!inThemeEditor() || !event.submitter || event.submitter.name !== 'checkout') return;
      event.preventDefault();
      event.stopPropagation();
      openCartTab();
    },
    true
  );

  class Cart {
    constructor() {
      this.state = { items: [], item_count: 0, total_price: 0 };
      this.listeners = new Set();
      this.pending = 0;
    }

    async request(path, body) {
      const response = await fetch(`${routesRoot}${path}`, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.description || data.message || 'The cart could not be updated.');
      }
      return data;
    }

    emit() {
      this.listeners.forEach((listener) => listener(this));
      document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: this } }));
    }

    subscribe(listener) {
      this.listeners.add(listener);
      return () => this.listeners.delete(listener);
    }

    get items() {
      return this.state.items || [];
    }

    get count() {
      return this.state.item_count || 0;
    }

    get subtotal() {
      return this.state.total_price || 0;
    }

    /** Ersparnis ggü. Streichpreis (_compare_at_price) inkl. automatischer Rabatte. */
    get savings() {
      return this.items.reduce((total, item) => {
        const compareUnit = parseInt(item.properties?._compare_at_price, 10) || 0;
        const baseline = Math.max(compareUnit * item.quantity, item.original_line_price);
        return total + Math.max(0, baseline - item.final_line_price);
      }, 0);
    }

    async refresh() {
      this.state = await this.request('cart.js');
      this.emit();
      return this.state;
    }

    /** @param {Array<{id:number, quantity:number, properties?:object}>} items */
    async add(items) {
      await this.request('cart/add.js', { items });
      return this.refresh();
    }

    async setQuantity(key, quantity) {
      const next = Math.max(0, Math.min(99, parseInt(quantity, 10) || 0));
      this.pending += 1;
      try {
        this.state = await this.request('cart/change.js', { id: key, quantity: next });
        this.emit();
      } finally {
        this.pending -= 1;
      }
    }

    remove(key) {
      return this.setQuantity(key, 0);
    }
  }

  Comfora.cart = new Cart();

  /* Warenkorb-Zähler im Header */
  const updateCartCount = () => {
    const count = Comfora.cart.count;
    document.querySelectorAll('[data-cart-count]').forEach((bubble) => {
      const previous = Number(bubble.dataset.count || 0);
      bubble.textContent = count > 0 ? String(count) : '';
      bubble.dataset.count = String(count);
      if (count > previous) {
        bubble.classList.remove('is-bumped');
        void bubble.offsetWidth; // Reflow, damit die Animation erneut startet
        bubble.classList.add('is-bumped');
      }
    });
    document.querySelectorAll('[data-cart-toggle]').forEach((button) => {
      button.setAttribute('aria-label', `Open cart (${count} items)`);
    });
  };

  /* Warenkorb-Drawer */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.dialog = this.closest('dialog');
      this.list = this.querySelector('[data-cart-list]');
      this.emptyState = this.querySelector('[data-cart-empty]');
      this.footer = this.dialog?.querySelector('[data-cart-footer]');
      this.template = document.getElementById('cart-item-template');
      if (!this.list || !this.template) return;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.dialog?.querySelector('[data-checkout-form]')?.addEventListener('submit', this.onCheckout.bind(this));

      this.unsubscribe = Comfora.cart.subscribe(() => this.render());
      this.render();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    render() {
      const cart = Comfora.cart;
      const active = document.activeElement;
      const focusKey = this.contains(active) ? active.closest('[data-key]')?.dataset.key : null;
      const focusRole = focusKey ? ['data-qty-minus', 'data-qty-plus', 'data-qty', 'data-remove'].find((attr) => active.hasAttribute(attr)) : null;

      this.list.replaceChildren(...cart.items.map((item) => this.renderItem(item)));

      const hasItems = cart.items.length > 0;
      this.list.hidden = !hasItems;
      this.emptyState.hidden = hasItems;
      if (this.footer) this.footer.hidden = !hasItems;

      const savings = cart.savings;
      this.dialog?.querySelectorAll('[data-cart-subtotal]').forEach((el) => (el.textContent = Comfora.formatMoney(cart.subtotal)));
      this.dialog?.querySelectorAll('[data-cart-savings]').forEach((el) => (el.textContent = `– ${Comfora.formatMoney(savings)}`));
      this.dialog?.querySelectorAll('[data-cart-savings-row]').forEach((el) => (el.hidden = savings <= 0));
      this.dialog?.querySelectorAll('[data-cart-count-label]').forEach((el) => (el.textContent = cart.count ? `(${cart.count})` : ''));

      if (focusKey && focusRole) {
        const target = this.list.querySelector(`[data-key="${CSS.escape(focusKey)}"] [${focusRole}]`);
        (target || this.dialog?.querySelector('[data-overlay-close]'))?.focus();
      }
    }

    renderItem(item) {
      const node = this.template.content.firstElementChild.cloneNode(true);
      node.dataset.key = item.key;

      node.querySelectorAll('[data-link]').forEach((link) => (link.href = item.url));
      const image = node.querySelector('[data-image]');
      if (item.image) {
        try {
          const url = new URL(item.image, window.location.origin);
          url.searchParams.set('width', '176');
          image.src = url.toString();
        } catch (error) {
          image.src = item.image;
        }
      } else {
        image.remove();
      }
      const variantTitle = Comfora.displayText(item.variant_title || '');
      image.alt = `${item.product_title} – ${variantTitle}`;

      node.querySelector('[data-title]').textContent = item.product_title;
      node.querySelector('[data-variant]').textContent = variantTitle;

      const discountTitles = (item.line_level_discount_allocations || [])
        .map((allocation) => allocation.discount_application && Comfora.displayText(allocation.discount_application.title))
        .filter(Boolean);
      const props = Object.entries(item.properties || {})
        .filter(([key, value]) => value && !key.startsWith('_'))
        .map(([key, value]) => `${key}: ${value}`);
      const meta = [...new Set([...discountTitles, ...props])].join(' · ');
      const propsEl = node.querySelector('[data-props]');
      propsEl.textContent = meta;
      propsEl.hidden = !meta;

      const qtyInput = node.querySelector('[data-qty]');
      qtyInput.value = item.quantity;
      qtyInput.setAttribute('aria-label', `Quantity for ${item.product_title}, ${variantTitle}`);

      node.querySelector('[data-price]').textContent = Comfora.formatMoney(item.final_line_price);
      const compareUnit = parseInt(item.properties?._compare_at_price, 10) || 0;
      const baseline = Math.max(compareUnit * item.quantity, item.original_line_price);
      const compareEl = node.querySelector('[data-compare]');
      if (baseline > item.final_line_price) {
        compareEl.textContent = Comfora.formatMoney(baseline);
      } else {
        compareEl.remove();
      }

      node.querySelector('[data-remove]').setAttribute('aria-label', `Remove ${item.product_title} (${variantTitle})`);
      return node;
    }

    async updateLine(key, quantity) {
      this.setAttribute('aria-busy', 'true');
      try {
        await Comfora.cart.setQuantity(key, quantity);
      } catch (error) {
        Comfora.toast(error.message);
        this.render();
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    /**
     * Checkout: regulärer POST an /cart mit name="checkout".
     * Im Theme-Editor läuft der Shop in einem iframe, in dem der Checkout nicht geladen werden darf –
     * dort öffnen wir ihn über einen Warenkorb-Permalink (/cart/ID:Menge,…) im obersten Fenster.
     */
    onCheckout(event) {
      if (!inThemeEditor() || !Comfora.cart.items.length) return;
      event.preventDefault();
      openCartTab();
    }

    onClick(event) {
      const line = event.target.closest('[data-key]');
      if (!line || this.getAttribute('aria-busy') === 'true') return;
      const key = line.dataset.key;
      const item = Comfora.cart.items.find((entry) => entry.key === key);
      if (!item) return;

      if (event.target.closest('[data-qty-minus]')) this.updateLine(key, item.quantity - 1);
      else if (event.target.closest('[data-qty-plus]')) this.updateLine(key, item.quantity + 1);
      else if (event.target.closest('[data-remove]')) this.updateLine(key, 0);
    }

    onChange(event) {
      if (!event.target.matches('[data-qty]')) return;
      const key = event.target.closest('[data-key]')?.dataset.key;
      if (key) this.updateLine(key, event.target.value);
    }
  }
  customElements.define('cart-items', CartItems);

  /* Bundle-Upsell im Warenkorb: zeigt die nächste Preisstufe (1 → 2 → 3 Paare) und fügt ein weiteres Paar hinzu.
     Preise pro Paar = automatische Rabatte „Comfora 2er-Set“ / „Comfora 3er-Set“ (gelten für Vela und Noma gemischt). */
  class CartUpsell extends HTMLElement {
    connectedCallback() {
      // Zwei Teile: data-part="progress" (Preisstufen oben im Warenkorb) und data-part="offer" (Karte unter den Artikeln).
      this.upsellPart = this.dataset.part || 'offer';
      this.products = Comfora.readJSON(document.querySelector('[data-upsell-products]')) || {};
      this.unit2 = parseInt(this.dataset.unitTwo, 10) || 0;
      this.unit3 = parseInt(this.dataset.unitThree, 10) || 0;
      this.card = this.querySelector('[data-upsell-card]');
      this.message = this.querySelector('[data-upsell-message]');
      this.bar = this.querySelector('[data-upsell-bar]');
      this.titleEl = this.querySelector('[data-upsell-title]');
      this.subEl = this.querySelector('[data-upsell-sub]');
      this.image = this.querySelector('[data-upsell-image]');
      this.colorSelect = this.querySelector('[data-upsell-color]');
      this.sizeSelect = this.querySelector('[data-upsell-size]');
      this.error = this.querySelector('[data-upsell-error]');
      this.button = this.querySelector('[data-upsell-add]');
      this.buttonLabel = this.querySelector('[data-upsell-label]');
      this.selectionKey = '';

      this.colorSelect?.addEventListener('change', () => {
        this.renderSizes(this.sizeSelect.value);
        this.updateImage();
        this.hideError();
      });
      this.sizeSelect?.addEventListener('change', () => this.hideError());
      this.button?.addEventListener('click', () => this.add());
      // Ein Fehler im Upsell darf den Warenkorb nie blockieren (cart.emit ruft die Listener synchron auf).
      this.safeRender = () => {
        try {
          this.render();
        } catch (error) {
          console.error('[Comfora] Upsell', error);
          this.hidden = true;
        }
      };
      this.unsubscribe = Comfora.cart.subscribe(this.safeRender);
      this.safeRender();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    unitFor(pairs, basePrice) {
      if (pairs >= 3 && this.unit3) return Math.min(this.unit3, basePrice);
      if (pairs === 2 && this.unit2) return Math.min(this.unit2, basePrice);
      return basePrice;
    }

    render() {
      const items = Comfora.cart.items.filter((item) => this.products[item.handle]);
      const pairs = items.reduce((total, item) => total + item.quantity, 0);
      this.hidden = pairs === 0 || (this.upsellPart === 'offer' && pairs >= 3);
      if (!pairs) return;

      const last = items[items.length - 1];
      this.product = this.products[last.handle];
      const lastVariant = this.product.variants.find((variant) => variant.id === last.variant_id);
      const basePrice = lastVariant?.price || last.original_price || last.price;
      const next = pairs + 1;
      const nextUnit = this.unitFor(next, basePrice);

      if (this.upsellPart === 'progress') {
        const tierPrice = this.querySelector('[data-tier-price="1"]');
        if (tierPrice) tierPrice.textContent = Comfora.formatMoney(basePrice);
        this.querySelectorAll('[data-tier]').forEach((tier) => {
          const level = Number(tier.dataset.tier);
          tier.classList.toggle('is-reached', pairs >= level);
          tier.classList.toggle('is-current', Math.min(pairs, 3) === level);
        });
        if (this.bar) this.bar.style.transform = `scaleX(${pairs >= 3 ? 1 : pairs === 2 ? 0.5 : 0.08})`;
        if (pairs >= 3) {
          this.message.innerHTML = `<svg aria-hidden="true"><use href="#icon-check-circle"/></svg><span><strong>Best price unlocked:</strong> every pair costs ${Comfora.formatMoney(this.unitFor(3, basePrice))}.</span>`;
        } else if (pairs === 1) {
          this.message.innerHTML = `<span>You're <strong>1 pair away</strong> from ${Comfora.formatMoney(nextUnit)} per pair.</span>`;
        } else {
          this.message.innerHTML = `<span>Just <strong>1 more pair</strong> and every pair costs ${Comfora.formatMoney(nextUnit)}.</span>`;
        }
        return;
      }

      if (pairs >= 3) return;
      const currentTotal = pairs * this.unitFor(pairs, basePrice);
      const extra = next * nextUnit - currentTotal;
      const totalSaving = next * basePrice - next * nextUnit;
      const ordinal = next === 2 ? '2nd' : '3rd';

      this.titleEl.textContent = `Add a ${ordinal} pair for just ${Comfora.formatMoney(extra)}`;
      this.subEl.textContent = `${next === 2 ? 'Both pairs' : 'All 3 pairs'} drop to ${Comfora.formatMoney(nextUnit)} each – you save ${Comfora.formatMoney(totalSaving)} in total. Mix colours and sizes as you like.`;
      this.buttonLabel.textContent = `Add ${ordinal} pair · +${Comfora.formatMoney(extra)}`;

      // Auswahl nur neu vorbelegen, wenn sich der Warenkorb geändert hat (nicht während der Kunde wählt).
      const key = `${last.key}:${pairs}`;
      if (key !== this.selectionKey) {
        this.selectionKey = key;
        this.renderColors(lastVariant?.option1);
        this.renderSizes(lastVariant?.option2);
        this.updateImage();
        this.hideError();
      }
    }

    renderColors(preferred) {
      const colors = [...new Set(this.product.variants.map((variant) => variant.option1))];
      this.colorSelect.replaceChildren(...colors.map((color) => new Option(Comfora.displayText(color), color)));
      this.colorSelect.value = colors.includes(preferred) ? preferred : colors[0];
    }

    renderSizes(preferred) {
      const color = this.colorSelect.value;
      const options = this.product.variants
        .filter((variant) => variant.option1 === color)
        .map((variant) => {
          const option = new Option(variant.available ? `Size ${variant.option2}` : `Size ${variant.option2} – sold out`, variant.option2);
          option.disabled = !variant.available;
          return option;
        });
      this.sizeSelect.replaceChildren(new Option('Choose size', ''), ...options);
      const match = options.find((option) => option.value === preferred && !option.disabled);
      this.sizeSelect.value = match ? preferred : '';
    }

    updateImage() {
      const src = this.product?.images?.[this.colorSelect.value];
      if (!this.image) return;
      if (src) {
        this.image.src = src;
        this.image.alt = `${this.product.title} in ${Comfora.displayText(this.colorSelect.value)}`;
        this.image.hidden = false;
      } else {
        this.image.hidden = true;
      }
    }

    showError(text) {
      this.error.textContent = text;
      this.error.hidden = false;
    }

    hideError() {
      if (this.error) this.error.hidden = true;
    }

    async add() {
      const variant = this.product?.variants.find((item) => item.option1 === this.colorSelect.value && item.option2 === this.sizeSelect.value);
      if (!variant || !variant.available) {
        this.showError('Please choose a size for the additional pair.');
        this.sizeSelect.focus();
        return;
      }
      if (this.button.getAttribute('aria-busy') === 'true') return;
      this.button.setAttribute('aria-busy', 'true');
      try {
        await Comfora.cart.add([{ id: variant.id, quantity: 1, properties: { _compare_at_price: String(variant.compare_at_price || variant.price) } }]);
        Comfora.toast('Pair added – your bundle price has been applied.');
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.button.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('cart-upsell', CartUpsell);

  Comfora.cart.subscribe(updateCartCount);
  Comfora.cart.refresh().catch((error) => console.error('[Comfora] Warenkorb', error));

  /* Videos mit data-autoplay-video: laden & spielen erst, wenn sie sichtbar sind (auch in Slidern), sonst Pause */
  const autoplayVideos = document.querySelectorAll('video[data-autoplay-video]');
  if (autoplayVideos.length) {
    if (Comfora.reducedMotion() || !('IntersectionObserver' in window)) {
      autoplayVideos.forEach((video) => (video.controls = true));
    } else {
      const videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting) video.play().catch(() => {});
            else video.pause();
          });
        },
        { threshold: 0.5 }
      );
      autoplayVideos.forEach((video) => videoObserver.observe(video));
    }
  }
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) Comfora.cart.refresh().catch(() => {});
  });

  /* ------------------------------------------------------------------------
     Announcement-Bar (rotiert auf Mobile, pausiert bei Hover/Fokus)
     ------------------------------------------------------------------------ */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.announcement__item'));
      if (this.items.length < 2) return;
      this.index = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
      this.paused = false;
      this.mobileQuery = window.matchMedia('(max-width: 989px)');

      const pause = () => (this.paused = true);
      const resume = () => (this.paused = false);
      this.addEventListener('mouseenter', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);

      this.timer = window.setInterval(() => this.next(), 4500);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    next() {
      if (this.paused || !this.mobileQuery.matches || Comfora.reducedMotion() || document.hidden) return;
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ------------------------------------------------------------------------
     Header (Schatten beim Scrollen)
     ------------------------------------------------------------------------ */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 8);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
  }
  customElements.define('site-header', SiteHeader);

  /* ------------------------------------------------------------------------
     Slider (Scroll-Snap + Pfeile + Punkte)
     ------------------------------------------------------------------------ */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.dots = this.querySelector('[data-slider-dots]');
      this.controls = this.querySelector('.slider-controls');

      this.prevButton?.addEventListener('click', () => this.move(-1));
      this.nextButton?.addEventListener('click', () => this.move(1));
      this.dots?.addEventListener('click', (event) => {
        const dot = event.target.closest('button[data-index]');
        if (dot) this.goTo(Number(dot.dataset.index));
      });

      this.onScroll = () => {
        window.cancelAnimationFrame(this.frame);
        this.frame = window.requestAnimationFrame(() => this.update());
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.resizeObserver = new ResizeObserver(() => this.update(true));
      this.resizeObserver.observe(this.track);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    get step() {
      if (this.slides.length > 1) {
        const distance = this.slides[1].offsetLeft - this.slides[0].offsetLeft;
        if (distance > 0) return distance;
      }
      return this.track.clientWidth || 1;
    }

    get pageCount() {
      const scrollable = this.track.scrollWidth - this.track.clientWidth;
      return scrollable > 2 ? Math.round(scrollable / this.step) + 1 : 1;
    }

    move(direction) {
      this.track.scrollBy({ left: direction * this.step, behavior: Comfora.scrollBehavior() });
    }

    goTo(index) {
      this.track.scrollTo({ left: index * this.step, behavior: Comfora.scrollBehavior() });
    }

    buildDots(count) {
      if (!this.dots) return;
      this.dots.replaceChildren(
        ...Array.from({ length: count }, (_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.dataset.index = String(index);
          dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
          return dot;
        })
      );
    }

    update(rebuild = false) {
      const pages = this.pageCount;
      if (this.controls) this.controls.hidden = pages <= 1;
      if (rebuild || (this.dots && this.dots.children.length !== pages)) this.buildDots(pages);

      const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prevButton) this.prevButton.disabled = this.track.scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll;

      const current = Math.min(pages - 1, Math.round(this.track.scrollLeft / this.step));
      this.dots?.querySelectorAll('button').forEach((dot, index) => {
        dot.setAttribute('aria-current', String(index === current));
      });
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ------------------------------------------------------------------------
     Countdown (bis Mitternacht, lokale Zeit)
     ------------------------------------------------------------------------ */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.output = this.querySelector('[data-countdown]') || this;
      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    tick() {
      const now = new Date();
      const end = new Date(now);
      end.setHours(24, 0, 0, 0);
      const seconds = Math.max(0, Math.floor((end - now) / 1000));
      const pad = (value) => String(value).padStart(2, '0');
      this.output.textContent = `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
    }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ------------------------------------------------------------------------
     Tabs (WAI-ARIA Tabs Pattern)
     ------------------------------------------------------------------------ */
  class TabsComponent extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });
    }

    select(index, focus = false) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (this.panels[i]) this.panels[i].hidden = !selected;
      });
      if (focus) this.tabs[index].focus();
    }

    onKeydown(event, index) {
      const last = this.tabs.length - 1;
      const map = { ArrowRight: index === last ? 0 : index + 1, ArrowLeft: index === 0 ? last : index - 1, Home: 0, End: last };
      if (!(event.key in map)) return;
      event.preventDefault();
      this.select(map[event.key], true);
    }
  }
  customElements.define('tabs-component', TabsComponent);

  /* ------------------------------------------------------------------------
     Feature-Accordion mit synchronem Bild („Anders gedacht")
     ------------------------------------------------------------------------ */
  class FeatureAccordion extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('details'));
      this.media = Array.from(this.querySelectorAll('[data-feature-media]'));
      this.items.forEach((details, index) => {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          this.items.forEach((other) => {
            if (other !== details) other.open = false;
          });
          this.media.forEach((item, i) => item.classList.toggle('is-active', i === index));
        });
      });
    }
  }
  customElements.define('feature-accordion', FeatureAccordion);

  /* ------------------------------------------------------------------------
     Produktkarten: Farb-Swatches wechseln das Bild
     ------------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('[data-card-swatch]');
    if (!swatch) return;
    event.preventDefault();
    const card = swatch.closest('.product-card');
    const image = card?.querySelector('[data-card-image]');
    if (!image) return;
    image.src = swatch.dataset.image;
    image.alt = swatch.dataset.alt || image.alt;
    const hoverImage = card.querySelector('[data-card-hover]');
    if (hoverImage) hoverImage.src = swatch.dataset.hoverImage || swatch.dataset.image;
    card.querySelectorAll('[data-card-swatch]').forEach((item) => item.setAttribute('aria-pressed', String(item === swatch)));
  });

  /* ------------------------------------------------------------------------
     Newsletter (Demo: nur Validierung + Erfolgsmeldung)
     ------------------------------------------------------------------------ */
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-newsletter]');
    if (!form) return;
    event.preventDefault();

    const input = form.querySelector('input[type="email"]');
    const message = form.querySelector('[data-form-message]');
    const valid = input.value.trim() !== '' && input.checkValidity();

    message.hidden = false;
    message.className = `form-message ${valid ? 'form-message--success' : 'form-message--error'}`;
    message.textContent = valid
      ? 'Thank you! Your 10% code is on its way to your inbox.'
      : 'Please enter a valid email address.';
    input.setAttribute('aria-invalid', String(!valid));

    if (valid) form.reset();
    else input.focus();
  });

  /* ------------------------------------------------------------------------
     Reveal-on-Scroll
     ------------------------------------------------------------------------ */
  const revealElements = document.querySelectorAll('[data-reveal]');
  if ('IntersectionObserver' in window && !Comfora.reducedMotion()) {
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.06 }
    );
    revealElements.forEach((element) => revealObserver.observe(element));
  } else {
    revealElements.forEach((element) => element.classList.add('is-revealed'));
  }

  /* Customer photo slideshow: pause / play */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-ugc-toggle]');
    if (!toggle) return;
    const slideshow = toggle.closest('ugc-slideshow');
    const paused = slideshow.classList.toggle('is-paused');
    toggle.setAttribute('aria-pressed', String(paused));
    toggle.setAttribute('aria-label', paused ? 'Play customer photos' : 'Pause customer photos');
  });

  /* Aktuelles Jahr im Footer */
  document.querySelectorAll('[data-year]').forEach((element) => (element.textContent = String(new Date().getFullYear())));
})();
